import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogProvider } from '@posthog/react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from './i18n';
import ErrorBoundary from './components/ErrorBoundary';
import AppRoutes from './routes/AppRoutes';
import { QuestionnaireProvider } from './contexts/QuestionnaireContext';
import { AuthProvider } from './contexts/AuthContext';
import { queryClient } from './app/queryClient';
import { useCompleteQuestionnaire } from './features/expat/hooks/useCompleteQuestionnaire';
import { FormData } from './types/questionnaire';
import { getPostHogClient } from './shared/posthog';
import './App.css';

/**
 * Full-screen spinner shown while i18next loads its initial translation
 * bundles (and any other Suspense-gated resource at the root of the app).
 * Keep this intentionally text-free: it renders before i18n is ready, so
 * calling t() here would either flash a key or require a second Suspense
 * boundary.
 */
const RootSuspenseFallback: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div
      className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
      aria-label="Loading"
      role="status"
    />
  </div>
);

/**
 * Inner app component that uses hooks
 */
const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const [savedData, setSavedData] = useState<FormData | null>(null);
  const [isQuestionnaireComplete, setIsQuestionnaireComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { mutateAsync: completeQuestionnaire } = useCompleteQuestionnaire();

  // Load saved data from localStorage on mount
  useEffect(() => {
    try {
      // Try to load completed questionnaire data
      const savedQuestionnaireData = localStorage.getItem('questionnaireData');
      if (savedQuestionnaireData) {
        const parsedData = JSON.parse(savedQuestionnaireData);
        setSavedData(parsedData);
        setIsQuestionnaireComplete(true);
      } else {
        // Try to load progress data
        const progressData = localStorage.getItem('questionnaire_progress');
        if (progressData) {
          const parsed = JSON.parse(progressData);
          if (parsed.formData) {
            setSavedData(parsed.formData);
            // Don't set complete if it's just progress
            setIsQuestionnaireComplete(false);
          }
        }
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleQuestionnaireComplete = async (data: FormData) => {
    setSavedData(data);
    setIsQuestionnaireComplete(true);
    console.log('Questionnaire completed with data:', data);
    
    // Submit to backend API using React Query hook
    try {
      await completeQuestionnaire({
        answers: data,
        calculateTax: false,
        returnTaxInput: true,
      });
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
    }
  };

  const handleSave = (data: FormData) => {
    setSavedData(data);
    console.log('Data saved:', data);
    localStorage.setItem('questionnaireData', JSON.stringify(data));
  };

  const handleStartNew = () => {
    setSavedData(null);
    setIsQuestionnaireComplete(false);
    localStorage.removeItem('questionnaireData');
    localStorage.removeItem('questionnaire_progress');
    // Navigate to questionnaire
    window.location.href = '/';
  };

  const handleImportData = (importedData: FormData) => {
    setSavedData(importedData);
    setIsQuestionnaireComplete(true);
    // Save to localStorage
    localStorage.setItem('questionnaireData', JSON.stringify(importedData));
  };

  // Show loading state while checking localStorage
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common:loading.generic')}</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
      <QuestionnaireProvider initialFormData={savedData || undefined}>
        <Router>
          <div className="h-full min-h-0 flex flex-col overflow-hidden bg-gray-50">
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <AppRoutes
              savedData={savedData}
              onStartNew={handleStartNew}
              onImportData={handleImportData}
            />
            </div>
          </div>
        </Router>
      </QuestionnaireProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

const posthogClient = getPostHogClient();

/**
 * Main App component with QueryClientProvider and PostHogProvider
 */
const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<RootSuspenseFallback />}>
        <QueryClientProvider client={queryClient}>
          {posthogClient ? (
            <PostHogProvider client={posthogClient}>
              <AppContent />
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            </PostHogProvider>
          ) : (
            <>
              <AppContent />
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            </>
          )}
        </QueryClientProvider>
      </Suspense>
    </I18nextProvider>
  );
};

export default App;
